import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { isEqual } from "lodash"
import { AutocompleteRenderOptionState } from "@mui/material"

import BasicButton from "@/components/button/BasicButton"
import Selector from "@/components/form/selector/Selector"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import { useReadACWRQuery, useUpsertACWRMutation } from "@/hooks/api/useSettingQuery"
import { createGlobalStore } from "@/stores/global"

import "./advanced.scss"

const ACWR_DAYS = [7, 14, 21, 28]

type ACWROptionType = {
  label: string
  value: number
}

export default function AdvancedPage() {
  const { t } = useTranslation()
  const [newACWR, setNewACWR] = useState({ acute: 0, chronic: 0, idfTeam: 0 })
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const { data } = useReadACWRQuery()
  const { mutateAsync } = useUpsertACWRMutation()

  useEffect(() => {
    if (data) {
      setNewACWR({ ...data })
    }
  }, [data])

  const getAWCROption = (option: number) => {
    return { label: `${option} ${t("days")}`, value: option }
  }

  const onSave = async () => {
    try {
      await mutateAsync(newACWR)
      setSnackBar({ message: t("messages.successUpdate"), severity: "success", open: true })
    } catch (error) {
      return error
    }
  }

  const isDisabledSaveBtn = useMemo(() => {
    return isEqual(newACWR, data)
  }, [newACWR, data])

  return (
    <div className="AdvancedCont">
      <h3 className="settings-section-title">{t("acuteChronicWorkloadRatio")}</h3>
      <div className="ACWRSelectorCont">
        <div className="selector-row">
          <div className="selector-label">{t("acuteDuration")}</div>
          <Selector
            options={ACWR_DAYS.map(getAWCROption)}
            value={
              ACWR_DAYS.map(getAWCROption).find((day) => day.value === newACWR.acute) ??
              getAWCROption(7)
            }
            renderOption={renderOption}
            onChange={(_: unknown, option: ACWROptionType) => {
              if (option) {
                setNewACWR({ ...newACWR, acute: option.value })
              }
            }}
          />
        </div>
        <div className="selector-row">
          <div className="selector-label">{t("chronicDuration")}</div>
          <Selector
            options={ACWR_DAYS.map(getAWCROption)}
            value={
              ACWR_DAYS.map(getAWCROption).find((day) => day.value === newACWR.chronic) ??
              getAWCROption(28)
            }
            renderOption={renderOption}
            onChange={(_: unknown, option: ACWROptionType) => {
              if (option) {
                setNewACWR({ ...newACWR, chronic: option.value })
              }
            }}
          />
        </div>
      </div>
      <div className="ACWRButtonCont">
        <BasicButton
          type="primary-contained"
          size="lg"
          onClick={onSave}
          disabled={isDisabledSaveBtn}>
          {t("button.save")}
        </BasicButton>
      </div>
    </div>
  )
}

const renderOption = (
  props: React.HTMLAttributes<HTMLLIElement> & { key: string },
  option: ACWROptionType,
  state?: AutocompleteRenderOptionState,
) => {
  const { key, ...prop } = props

  return (
    <li key={key} {...prop}>
      {option.label}
      {option.label === state?.inputValue && (
        <Icon type="check" size={16} color={colors.neutral.grey[1000]} />
      )}
    </li>
  )
}
